import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpIcon, ArrowDownIcon, ChevronDownIcon, CheckIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import MetricsChart from '../shared/MetricsChart';

function formatNumber(num) {
  return new Intl.NumberFormat().format(num);
}

// Compact human format for mobile: 1.2K / 3.4M / 1.1B (1 decimal, trimmed).
function formatCompact(num) {
  const n = Number(num) || 0;
  const abs = Math.abs(n);
  const fmt = (v, suffix) => {
    const s = v.toFixed(1).replace(/\.0$/, '');
    return `${s}${suffix}`;
  };
  if (abs >= 1e9) return fmt(n / 1e9, 'B');
  if (abs >= 1e6) return fmt(n / 1e6, 'M');
  if (abs >= 1e3) return fmt(n / 1e3, 'K');
  return String(n);
}

function formatPercent(num, showPlus = false) {
  if (num === null || num === undefined) return '';
  return `${showPlus && num > 0 ? '+' : ''}${num.toFixed(1)}%`;
}

function formatDate(value) {
  if (!value) return '';
  // Backend sends ISO datetime; show YYYY-MM-DD for compactness.
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

// V2: render % change as a compact pill (green up / red down / neutral),
// intensity scaling with magnitude. Cleaner than tinting the whole cell.
function getPercentPill(percent) {
  if (percent === null || percent === undefined) return 'bg-gray-100 text-gray-400';
  const m = Math.abs(percent);
  if (percent > 0) {
    if (m < 5) return 'bg-green-50 text-green-700';
    if (m < 25) return 'bg-green-100 text-green-700';
    return 'bg-green-200 text-green-800';
  }
  if (percent < 0) {
    if (m < 5) return 'bg-red-50 text-red-700';
    if (m < 25) return 'bg-red-100 text-red-700';
    return 'bg-red-200 text-red-800';
  }
  return 'bg-gray-100 text-gray-500';
}

// "Weeks grown" cell tint — gradual green shades, darker as the count rises.
// Zero/empty stays transparent so it blends with the row background.
function getWeeksGrownColor(weeks) {
  if (weeks === null || weeks === undefined || weeks === 0) return '';
  if (weeks >= 10) return 'bg-green-600';
  if (weeks >= 8) return 'bg-green-500';
  if (weeks >= 6) return 'bg-green-400';
  if (weeks >= 4) return 'bg-green-300';
  if (weeks >= 2) return 'bg-green-200';
  return 'bg-green-100';
}

// Boolean (peak) cell tint — green when true, transparent otherwise.
function getBooleanColor(value) {
  return value === true ? 'bg-green-200' : '';
}

function getTrackingLabelColor(label) {
  if (!label) return 'bg-gray-100 text-gray-800';

  switch (label.toLowerCase()) {
    case 'high priority':
      return 'bg-red-100 text-red-800';
    case 'medium priority':
      return 'bg-yellow-100 text-yellow-800';
    case 'low priority':
      return 'bg-green-100 text-green-800';
    case 'tracking':
      return 'bg-blue-100 text-blue-800';
    case 'archive':
      return 'bg-gray-200 text-gray-700';
    case 'agenda - legal':
      return 'bg-yellow-100 text-yellow-800';
    case 'agenda - priorities':
      return 'bg-orange-100 text-orange-800';
    case 'agenda - new':
      return 'bg-blue-100 text-blue-800';
    case 'agenda - tracking':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// Compact display name for a user: the part of the email before '@'.
function emailName(email) {
  if (!email) return '';
  return String(email).split('@')[0];
}

// A column is filterable if it's numeric (streams/%/counts) or boolean, OR the
// tracking_label (Priority) column, which gets a categorical Priority +
// Tracked-by filter. Plain text columns are excluded.
function isFilterableColumn(column) {
  if (!column || !column.key) return false;
  if (['artist', 'name', 'title'].includes(column.key)) return false;
  return true;
}

// Popover rendered in a portal so it's never clipped by the table's overflow
// container. Positioned absolutely to the funnel button via anchorRect.
// Requires an explicit Apply click — typing alone does not refetch.
function ColumnFilterPopover({ column, value, anchorRect, onApply, onClose, pctColumn, pctValue }) {
  const isBool = !!column.isBoolean;
  const isDate = !!column.isDate;
  const [min, setMin] = useState(value?.min ?? '');
  const [max, setMax] = useState(value?.max ?? '');
  // Second min/max pair for the % partner of a merged column.
  const [pctMin, setPctMin] = useState(pctValue?.min ?? '');
  const [pctMax, setPctMax] = useState(pctValue?.max ?? '');
  // For boolean columns: 'any' | 'true' | 'false'.
  const initialBool = value?.eq === true ? 'true' : value?.eq === false ? 'false' : 'any';
  const [boolChoice, setBoolChoice] = useState(initialBool);
  const popoverRef = useRef(null);

  // Reset local state if the column/value changes underneath us.
  useEffect(() => {
    setMin(value?.min ?? '');
    setMax(value?.max ?? '');
    setPctMin(pctValue?.min ?? '');
    setPctMax(pctValue?.max ?? '');
    setBoolChoice(value?.eq === true ? 'true' : value?.eq === false ? 'false' : 'any');
  }, [column.key, value?.min, value?.max, value?.eq, pctValue?.min, pctValue?.max]);

  useEffect(() => {
    function handler(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleApply = (e) => {
    e.stopPropagation();
    if (isBool) {
      if (boolChoice === 'true') onApply(column.key, { eq: true });
      else if (boolChoice === 'false') onApply(column.key, { eq: false });
      else onApply(column.key, {});
    } else if (isDate) {
      const next = {};
      if (min !== '') next.min = min; // keep as 'YYYY-MM-DD'
      if (max !== '') next.max = max;
      onApply(column.key, next);
    } else {
      const next = {};
      if (min !== '' && !Number.isNaN(Number(min))) next.min = Number(min);
      if (max !== '' && !Number.isNaN(Number(max))) next.max = Number(max);
      onApply(column.key, next);
      if (pctColumn) {
        const pNext = {};
        if (pctMin !== '' && !Number.isNaN(Number(pctMin))) pNext.min = Number(pctMin);
        if (pctMax !== '' && !Number.isNaN(Number(pctMax))) pNext.max = Number(pctMax);
        onApply(pctColumn.key, pNext);
      }
    }
    onClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setMin('');
    setMax('');
    setPctMin('');
    setPctMax('');
    setBoolChoice('any');
    onApply(column.key, {});
    if (pctColumn) onApply(pctColumn.key, {});
    onClose();
  };

  if (!anchorRect) return null;

  // Anchor below the funnel button, right-aligned so it doesn't overflow off
  // the right edge of the viewport on the rightmost columns.
  const POPOVER_WIDTH = 200;
  const top = anchorRect.bottom + 4;
  const preferredLeft = anchorRect.right - POPOVER_WIDTH;
  const left = Math.max(8, Math.min(preferredLeft, window.innerWidth - POPOVER_WIDTH - 8));

  return createPortal(
    <div
      ref={popoverRef}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top, left, width: POPOVER_WIDTH, zIndex: 50 }}
      className="bg-white border border-gray-200 rounded-md shadow-lg p-3 normal-case tracking-normal"
    >
      <div className="text-[11px] font-medium text-gray-700 mb-2">
        Filter {column.label}
        {column.isPercent ? ' (%)' : ''}
      </div>
      {isBool && (
        <div className="flex flex-col gap-2">
          <div className="flex rounded border border-gray-300 overflow-hidden">
            {[
              { key: 'any', label: 'Any' },
              { key: 'true', label: 'Yes' },
              { key: 'false', label: 'No' },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={(e) => { e.stopPropagation(); setBoolChoice(opt.key); }}
                className={`flex-1 px-2 py-1 text-[11px] ${
                  boolChoice === opt.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center pt-1">
            <button
              type="button"
              onClick={handleClear}
              className="text-[10px] text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="text-[11px] px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
      {isDate && (
        <div className="flex flex-col gap-2">
          <label className="text-[10px] text-gray-500">
            From
            <input
              type="date"
              value={min}
              onChange={(e) => setMin(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
          <label className="text-[10px] text-gray-500">
            To
            <input
              type="date"
              value={max}
              onChange={(e) => setMax(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
          <div className="flex justify-between items-center pt-1">
            <button
              type="button"
              onClick={handleClear}
              className="text-[10px] text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="text-[11px] px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
      {!isBool && !isDate && (
        <div className="flex flex-col gap-2">
          {pctColumn && (
            <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Streams</div>
          )}
          <label className="text-[10px] text-gray-500">
            Min
            <input
              type="number"
              step="any"
              value={min}
              onChange={(e) => setMin(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
          <label className="text-[10px] text-gray-500">
            Max
            <input
              type="number"
              step="any"
              value={max}
              onChange={(e) => setMax(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>

          {pctColumn && (
            <>
              <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide pt-1 mt-1 border-t border-gray-100">% Change</div>
              <label className="text-[10px] text-gray-500">
                Min %
                <input
                  type="number"
                  step="any"
                  value={pctMin}
                  onChange={(e) => setPctMin(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="text-[10px] text-gray-500">
                Max %
                <input
                  type="number"
                  step="any"
                  value={pctMax}
                  onChange={(e) => setPctMax(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            </>
          )}

          <div className="flex justify-between items-center pt-1">
            <button
              type="button"
              onClick={handleClear}
              className="text-[10px] text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="text-[11px] px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default function StreamingTable({
  queryKey,
  apiEndpoint,
  columns,
  sortConfig,
  onSort,
  onRowClick,
  expandedRow,
  onTrackingLabelChange,
  editingTrackingLabel,
  setEditingTrackingLabel,
  trackingLabelOptions,
  userOptions = null,          // [{id, email}] users who can be set as "updated by"; null hides the control
  onUpdatedByChange = null,    // (itemId, userId|null) => void
  renderExpandedContent,
  type,
  selectedRegion: initialSelectedRegion,
  showArchived,
  setShowArchived,
  onAddClick,
  group,
  audioOnly = false,
  lagDays = '2',
  hideAddButton = false,
  columnFilters = {},
  onColumnFiltersChange,
  selectedIds = [],
  onSelectionChange,
  enableSelection = true,
  // When set (a user id string), force a tracked_by_in filter to that user —
  // used by the page-level "My Dashboard" toggle. Overrides any Set By column
  // filter so the toggle wins regardless of per-column state.
  forcedTrackedBy = null,
}) {
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [timeRange, setTimeRange] = useState(30);
  const [selectedMetrics, setSelectedMetrics] = useState(['audio']);
  const [isMetricsOpen, setIsMetricsOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(initialSelectedRegion || 'global');
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  // Expanded-row tabs, albums only: 'metrics' or 'songs'.
  const [expandedRowTab, setExpandedRowTab] = useState('metrics');
  const [albumSongsSortConfig, setAlbumSongsSortConfig] = useState({ key: 'global_last_complete_week_streams', order: 'desc' });
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterAnchorRect, setFilterAnchorRect] = useState(null);
  // Merged-column sort dropdown (mobile): which column's menu is open + anchor.
  const [sortMenuCol, setSortMenuCol] = useState(null);
  const [sortMenuRect, setSortMenuRect] = useState(null);
  const observerTarget = useRef(null);
  // V2: mobile breakpoint (Tailwind `sm` = 640px). On mobile the artist and
  // song/album columns are merged into a single pinned column.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // For song/album tables on mobile, drop the standalone title column — its
  // content is folded into the artist column instead.
  const mergeTitleIntoArtist = isMobile && (type === 'song' || type === 'album');
  // On mobile, the priority (tracking_label) badge folds into the first pinned
  // column too, so its standalone column is dropped.
  const hasTrackingColumn = columns.some((c) => c.key === 'tracking_label');
  const mergeTrackingIntoFirst = isMobile && hasTrackingColumn;

  // A % change column that immediately follows a streams column is its partner
  // (e.g. "Global Building" + "Global Building %"). On mobile these fold into a
  // single column: streams value on top, % pill below. The merged column keeps
  // the streams key as primary and records the partner's key as `pctKey`.
  const isChangePctColumn = (c) =>
    c && c.isPercent && c.label !== 'US %' && /change_percent$/.test(c.key);

  const displayColumns = useMemo(() => {
    let cols = mergeTitleIntoArtist ? columns.filter((c) => c.key !== 'title') : columns;
    if (mergeTrackingIntoFirst) cols = cols.filter((c) => c.key !== 'tracking_label');
    if (!isMobile) return cols;
    const merged = [];
    for (let i = 0; i < cols.length; i++) {
      const col = cols[i];
      const next = cols[i + 1];
      if (!isChangePctColumn(col) && next && isChangePctColumn(next)) {
        merged.push({ ...col, pctKey: next.key, pctLabel: next.label, merged: true });
        i++; // skip the % column — it's folded in
      } else {
        merged.push(col);
      }
    }
    return merged;
  }, [columns, mergeTitleIntoArtist, mergeTrackingIntoFirst, isMobile]);

  // Tapping a merged streams/% header opens a dropdown to pick which sub-metric
  // (Value or % Change) and direction to sort by.
  const openMergedSortMenu = (col, e) => {
    e.stopPropagation();
    if (sortMenuCol?.key === col.key) {
      setSortMenuCol(null);
      setSortMenuRect(null);
    } else {
      setSortMenuRect(e.currentTarget.getBoundingClientRect());
      setSortMenuCol(col);
    }
  };
  const applyMergedSort = (key, order) => {
    onSort(key, order);
    setSortMenuCol(null);
    setSortMenuRect(null);
  };

  // Column width — tightened on mobile so more columns fit before scrolling.
  // Header and body MUST use the same value (table-fixed relies on it).
  // "updated by" adds a second control under the priority pill, so that column
  // needs to be a bit wider when the control is enabled.
  const trackingColW = onUpdatedByChange ? 'w-[130px]' : 'w-[100px]';
  const colWidth = (column) => {
    if (isMobile) {
      if (column.key === 'artist' || column.key === 'name' || column.key === 'title') return 'w-[120px]';
      if (column.key === 'tracking_label') return onUpdatedByChange ? 'w-[110px]' : 'w-[84px]';
      if (column.merged) return 'w-[84px]';
      if (column.isPercent || column.isBoolean) return 'w-[60px]';
      if (column.key === 'song_count') return 'w-[48px]';
      if (column.isDate) return 'w-[84px]';
      return 'w-[78px]';
    }
    if (column.key === 'artist' || column.key === 'name') return 'w-[180px]';
    if (column.key === 'title') return 'w-[200px]';
    if (column.merged) return 'w-[110px]';
    if (column.key === 'tracking_label') return trackingColW;
    if (column.isDate) return 'w-[100px]';
    if (column.isPercent || column.isBoolean) return 'w-[80px]';
    if (column.key === 'song_count') return 'w-[60px]';
    return 'w-[120px]';
  };

  // "Updated by" display + picker, shown under the priority pill. Shows who
  // last updated (falls back to who first tracked). The dropdown lets an
  // unsigned-access user reassign it. Renders nothing when the feature is off.
  const renderUpdatedBy = (item) => {
    if (!onUpdatedByChange) return null;
    const current = item.updated_by || item.tracked_by || null;
    // Sentinel value for the "clear it" option (distinct from the currently
    // selected user id, so selecting it always fires an unset).
    const CLEAR = '__clear__';
    return (
      <div className="mt-1 flex justify-center">
        <select
          value={current ? String(current.id) : ''}
          onChange={(e) => {
            e.stopPropagation();
            const v = e.target.value;
            onUpdatedByChange(item.id, (v && v !== CLEAR) ? Number(v) : null);
          }}
          onClick={(e) => e.stopPropagation()}
          title={current ? `Updated by ${current.email}` : 'Set updated by'}
          className={`appearance-none text-center text-[9px] font-medium rounded-full ring-1 ring-inset px-2 py-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-full truncate ${
            current ? 'bg-blue-50 text-blue-700 ring-blue-100' : 'bg-gray-50 text-gray-400 ring-gray-200'
          }`}
        >
          <option value="">{current ? emailName(current.email) : '+ set by'}</option>
          {current && <option value={CLEAR}>— none —</option>}
          {(userOptions || []).map((u) => (
            // Deactivated users appear (they may still be the current Set By)
            // but can't be assigned new work — the API rejects them.
            <option key={u.id} value={u.id} disabled={u.is_active === false}>
              {emailName(u.email)}{u.is_active === false ? ' (former)' : ''}
            </option>
          ))}
        </select>
      </div>
    );
  };
  // V2: track the horizontal scroll container width so the expanded row can be
  // pinned to the viewport (stays put while the table scrolls sideways).
  // A callback ref is used (not a mount effect) because the scroll container
  // is mounted AFTER the initial loading-spinner render — a [] effect would
  // run while the ref is still null and never re-run, leaving width at 0.
  const scrollContainerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const setScrollContainer = useCallback((el) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    scrollContainerRef.current = el;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    ro.observe(el);
    resizeObserverRef.current = ro;
  }, []);

  useEffect(() => () => {
    if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
  }, []);

  // V2: the first column (artist/name/title) is pinned to the left edge so it
  // stays visible during horizontal scroll on mobile.
  const pinnedHeaderStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 11,
    background: '#f1f5f9', // solid slate-100, opaque so columns don't bleed through
    boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
  };
  const pinnedCellStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 5,
    boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
  };

  // Metric options
  const metricOptions = [
    { id: 'total', label: 'Total Streams' },
    { id: 'audio', label: 'Audio Streams' },
    { id: 'video', label: 'Video Streams' },
    { id: 'ad', label: 'Ad-Supported' },
    { id: 'premium', label: 'Premium' },
    { id: 'ondemand', label: 'On-Demand' },
    { id: 'programmed', label: 'Programmed' }
  ];

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      }
      return [...prev, metricId];
    });
  };

  // Get the label of the first selected metric for display
  const selectedMetricLabel = metricOptions.find(opt => selectedMetrics.includes(opt.id))?.label || 'Select Type';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(filter), 500);
    return () => clearTimeout(timer);
  }, [filter]);

  // Build the request params from columnFilters: min_/max_/eq_ for numeric
  // columns, and tracking_label_in / tracked_by_in for the Priority column's
  // categorical filter.
  const numericFilterParams = useMemo(() => {
    const out = {};
    Object.entries(columnFilters || {}).forEach(([key, range]) => {
      if (!range) return;
      if (key === 'tracking_label' || key === 'updated_by') {
        if (Array.isArray(range.labels) && range.labels.length) {
          out.tracking_label_in = range.labels.join(',');
        }
        if (Array.isArray(range.userIds) && range.userIds.length) {
          out.tracked_by_in = range.userIds.join(',');
        }
        return;
      }
      if (range.min !== undefined && range.min !== null && range.min !== '') {
        out[`min_${key}`] = range.min;
      }
      if (range.max !== undefined && range.max !== null && range.max !== '') {
        out[`max_${key}`] = range.max;
      }
      if (range.eq !== undefined && range.eq !== null) {
        out[`eq_${key}`] = range.eq === true ? 'true' : range.eq === false ? 'false' : range.eq;
      }
    });
    // "My Dashboard" override: force Set By to the current user, winning over any
    // Set By column filter above.
    if (forcedTrackedBy) {
      out.tracked_by_in = String(forcedTrackedBy);
    }
    return out;
  }, [columnFilters, forcedTrackedBy]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery({
    queryKey: [
      ...queryKey,
      debouncedFilter,
      sortConfig,
      selectedRegion,
      group,
      showArchived,
      audioOnly,
      lagDays,
      numericFilterParams,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get(apiEndpoint, {
        params: {
          page: pageParam,
          per_page: 20,
          artist: debouncedFilter || undefined,
          sort_by: sortConfig?.key,
          sort_order: sortConfig?.order,
          show_archived: showArchived,
          group: group,
          audio_only: audioOnly,
          lag_days: lagDays,
          ...numericFilterParams,
        }
      });
      return response.data;
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page < Math.ceil(lastPage.total / lastPage.per_page)
        ? lastPage.page + 1
        : undefined;
    }
  });

  // Flattened list of currently-loaded items (across infinite-scroll pages).
  const loadedItems = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((p) => p.data || []);
  }, [data]);

  const loadedIds = useMemo(() => loadedItems.map((i) => i.id), [loadedItems]);
  const allLoadedSelected = enableSelection && loadedIds.length > 0 && loadedIds.every((id) => selectedIds.includes(id));
  const someLoadedSelected = enableSelection && !allLoadedSelected && loadedIds.some((id) => selectedIds.includes(id));

  const toggleSelectAllLoaded = (e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    if (allLoadedSelected) {
      onSelectionChange(selectedIds.filter((id) => !loadedIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selectedIds, ...loadedIds]));
      onSelectionChange(merged);
    }
  };

  const toggleSelectRow = (id, e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const updateColumnFilter = (key, range) => {
    if (!onColumnFiltersChange) return;
    const next = { ...(columnFilters || {}) };
    const hasValue = range && (
      range.min !== undefined || range.max !== undefined || range.eq !== undefined ||
      (Array.isArray(range.labels) && range.labels.length) ||
      (Array.isArray(range.userIds) && range.userIds.length)
    );
    if (!hasValue) {
      delete next[key];
    } else {
      next[key] = range;
    }
    onColumnFiltersChange(next);
  };

  // Add metrics query
  const { data: metrics, isFetching: isMetricsFetching } = useQuery(
    ['metrics', type, expandedRow, timeRange, selectedRegion, viewMode],
    async () => {
      if (!expandedRow) return null;
      let metricsEndpoint;
      const baseUrl = apiEndpoint.split('/song-summaries')[0].split('/unsigned-artist-summaries')[0].split('/unsigned-artist-aggregated')[0].split('/unsigned-agenda')[0].split('/unsigned-album-summaries')[0].split('/album-summaries')[0];

      if (type === 'artist-aggregated' || type === 'agenda') {
        // For aggregated artists, use the artist name (which is the id) URL-encoded
        const encodedArtistName = encodeURIComponent(expandedRow);
        metricsEndpoint = viewMode === 'weekly'
          ? `${baseUrl}/unsigned-artist-aggregated-metrics-weekly/${encodedArtistName}`
          : `${baseUrl}/unsigned-artist-aggregated-metrics/${encodedArtistName}`;
      } else if (viewMode === 'weekly') {
        if (type === 'song') {
          metricsEndpoint = `${baseUrl}/song-metrics-weekly/${expandedRow}`;
        } else if (type === 'album') {
          metricsEndpoint = `${baseUrl}/album-metrics-weekly/${expandedRow}`;
        } else {
          metricsEndpoint = `${baseUrl}/artist-metrics-weekly-direct/${expandedRow}`;
        }
      } else {
        if (type === 'song') {
          metricsEndpoint = `${baseUrl}/song-metrics/${expandedRow}`;
        } else if (type === 'album') {
          metricsEndpoint = `${baseUrl}/album-metrics/${expandedRow}`;
        } else {
          metricsEndpoint = `${baseUrl}/artist-metrics-direct/${expandedRow}`;
        }
      }

      const response = await axios.get(metricsEndpoint, {
        params: {
          days: timeRange,
          region: selectedRegion
        }
      });
      return response.data;
    },
    { enabled: !!expandedRow }
  );

  // Reset to the metrics tab whenever a different row is expanded.
  useEffect(() => {
    setExpandedRowTab('metrics');
    setAlbumSongsSortConfig({ key: 'global_last_complete_week_streams', order: 'desc' });
  }, [expandedRow]);

  // Songs on the expanded album, via the song_albums tracklist.
  const { data: albumSongs, isFetching: isAlbumSongsFetching } = useQuery(
    ['album-tracks', expandedRow, albumSongsSortConfig],
    async () => {
      if (!expandedRow || type !== 'album' || expandedRowTab !== 'songs') return null;
      const baseUrl = apiEndpoint.split('/song-summaries')[0].split('/unsigned-artist-summaries')[0].split('/unsigned-artist-aggregated')[0].split('/unsigned-agenda')[0].split('/unsigned-album-summaries')[0].split('/album-summaries')[0];

      const response = await axios.get(`${baseUrl}/album-tracks/${expandedRow}`, {
        params: {
          sort_by: albumSongsSortConfig.key,
          sort_order: albumSongsSortConfig.order
        }
      });
      return response.data;
    },
    { enabled: !!(expandedRow && type === 'album' && expandedRowTab === 'songs') }
  );

  const handleAlbumSongsSort = (key) => {
    setAlbumSongsSortConfig(prev => ({
      key,
      order: prev.key === key && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const AlbumSongsSortIcon = ({ columnKey }) => {
    if (albumSongsSortConfig.key !== columnKey) return null;
    return albumSongsSortConfig.order === 'desc'
      ? <ArrowDownIcon className="h-3 w-3 inline ml-1" />
      : <ArrowUpIcon className="h-3 w-3 inline ml-1" />;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.order === 'desc' ?
      <ArrowDownIcon className="h-4 w-4" /> :
      <ArrowUpIcon className="h-4 w-4" />;
  };

  // Update the expanded content rendering
  const renderMetricsContent = (item) => (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 w-full">
          <select
            value={timeRange}
            onChange={e => setTimeRange(Number(e.target.value))}
            className="w-1/3 min-w-0 px-2 sm:px-4 py-2 border rounded-lg text-xs sm:text-sm"
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last year</option>
            <option value={9999}>All Time</option>
          </select>

          <select
            value={selectedRegion}
            onChange={e => setSelectedRegion(e.target.value)}
            className="w-1/3 min-w-0 px-2 sm:px-4 py-2 border rounded-lg text-xs sm:text-sm"
          >
            <option value="global">Global</option>
            <option value="us">US</option>
          </select>

          <select
            value={viewMode}
            onChange={e => setViewMode(e.target.value)}
            className="w-1/3 min-w-0 px-2 sm:px-4 py-2 border rounded-lg text-xs sm:text-sm"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>

        <div className="relative">
          <button
            onClick={() => setIsMetricsOpen(!isMetricsOpen)}
            className="w-full px-4 py-2 border rounded-lg text-sm text-blue-600 bg-white flex justify-between items-center"
          >
            {selectedMetricLabel}
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </button>

          {isMetricsOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg">
              {metricOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => toggleMetric(option.id)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                    selectedMetrics.includes(option.id) ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                  {selectedMetrics.includes(option.id) && (
                    <CheckIcon className="h-5 w-5 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="h-[300px] sm:h-[400px] w-full">
        {isMetricsFetching && !metrics ? (
          <div className="flex flex-col items-center justify-center h-full w-full gap-3 text-gray-400">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            <span className="text-sm">Loading chart…</span>
          </div>
        ) : metrics ? (
          <MetricsChart
            dates={metrics.dates}
            metrics={metrics.metrics}
            selectedMetrics={selectedMetrics}
            percentChanges={metrics.percent_changes}
          />
        ) : (
          <div className="flex items-center justify-center h-full w-full text-sm text-gray-400">
            No chart data available
          </div>
        )}
      </div>
    </div>
  );

  const activeFilterCount = Object.values(columnFilters || {}).filter(
    (r) => r && (
      r.min !== undefined || r.max !== undefined || r.eq !== undefined ||
      (Array.isArray(r.labels) && r.labels.length) ||
      (Array.isArray(r.userIds) && r.userIds.length)
    )
  ).length;

  return (
    <div>
      {/* Controls Bar — single row: search shares the row with Archive + Add. */}
      <div className="flex flex-row items-center gap-2 sm:gap-4 mb-4 px-2 sm:px-0">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Search ${type === 'song' ? 'songs' : type === 'album' ? 'albums' : 'artists'}...`}
            className="w-full px-3 sm:px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 focus:bg-white transition"
          />
        </div>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => onColumnFiltersChange && onColumnFiltersChange({})}
              className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              Clear {activeFilterCount}
            </button>
          )}

          <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="sm:hidden">Archive</span>
            <span className="hidden sm:inline">Show Archived</span>
          </label>

          {!hideAddButton && (
            <button
              onClick={onAddClick}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium text-xs sm:text-sm py-2 px-2.5 sm:px-4 rounded-lg shadow-sm hover:shadow transition flex items-center whitespace-nowrap"
            >
              <svg className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Add {type === 'song' ? 'Song' : type === 'album' ? 'Album' : 'Artist'}</span>
              <span className="sm:hidden ml-0.5">{type === 'album' ? 'Album' : type === 'artist' ? 'Artist' : 'Song'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Table content */}
      <div className="overflow-x-auto border-y sm:border border-gray-200/80 sm:rounded-xl" ref={setScrollContainer}>
        <table className="min-w-full bg-white table-fixed">
          <thead className="bg-slate-50/80 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              {enableSelection && (
                <th className="px-2 py-2.5 w-[36px] text-left bg-slate-50/80">
                  <input
                    type="checkbox"
                    checked={allLoadedSelected}
                    ref={(el) => { if (el) el.indeterminate = someLoadedSelected; }}
                    onChange={toggleSelectAllLoaded}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    title="Select all loaded rows"
                  />
                </th>
              )}
              {displayColumns.map((column, colIdx) => {
                const filterable = isFilterableColumn(column);
                // Merged columns are "active" when either the streams or % key
                // is the current sort / has a filter.
                const sortActiveOnPct = column.merged && sortConfig?.key === column.pctKey;
                const hasActiveFilter = !!columnFilters?.[column.key] || (column.merged && !!columnFilters?.[column.pctKey]);
                const isPinned = colIdx === 0;
                return (
                  <th
                    key={column.key}
                    onClick={(e) => column.sortable && (column.merged ? openMergedSortMenu(column, e) : onSort(column.key))}
                    style={isPinned ? pinnedHeaderStyle : undefined}
                    className={`relative ${isMobile ? 'px-2 py-2' : 'px-2.5 py-2.5'} text-${column.align || 'left'} text-[10px] font-semibold text-gray-500 uppercase tracking-wider transition-colors ${column.sortable ? 'cursor-pointer hover:text-gray-700 hover:bg-slate-100/70' : ''} whitespace-nowrap ${isPinned ? 'bg-slate-50/80' : ''} ${colWidth(column)}`}
                  >
                    <div className={`flex items-center ${column.align === 'right' ? 'justify-end' : ''} gap-1`}>
                      {mergeTitleIntoArtist && colIdx === 0
                        ? (type === 'album' ? 'Artist / Album' : 'Artist / Song')
                        : column.label}
                      {/* Merged column: show which sub-metric is the active sort key. */}
                      {column.merged && (sortConfig?.key === column.key || sortActiveOnPct) && (
                        <span className="text-[8px] font-bold text-blue-600 normal-case">
                          {sortActiveOnPct ? '%' : '#'}
                        </span>
                      )}
                      {column.sortable && <SortIcon columnKey={column.merged && sortActiveOnPct ? column.pctKey : column.key} />}
                      {column.merged && <ChevronDownIcon className="h-3 w-3 text-gray-400" />}
                      {filterable && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openFilterKey === column.key) {
                              setOpenFilterKey(null);
                              setFilterAnchorRect(null);
                            } else {
                              setFilterAnchorRect(e.currentTarget.getBoundingClientRect());
                              setOpenFilterKey(column.key);
                            }
                          }}
                          className={`ml-1 p-0.5 rounded hover:bg-gray-200 ${hasActiveFilter ? 'text-blue-600' : 'text-gray-400'}`}
                          title={hasActiveFilter ? 'Filter active' : 'Filter column'}
                        >
                          <FunnelIcon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {data?.pages.map((page, i) => (
              <React.Fragment key={i}>
                {page.data.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      onClick={() => onRowClick(item)}
                      className={`group transition-colors hover:bg-blue-50/40 cursor-pointer ${item.tracking_label === 'Archive' ? 'opacity-60 bg-gray-100' : ''} ${expandedRow === item.id ? 'bg-blue-50/60' : ''} ${selectedIds.includes(item.id) ? 'bg-blue-50' : ''}`}
                    >
                      {enableSelection && (
                        <td className="px-2 py-1 w-[36px]" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={(e) => toggleSelectRow(item.id, e)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      )}
                      {displayColumns.map((column, colIdx) => {
                        const isPinned = colIdx === 0;
                        // Sticky cells need an opaque background so scrolled
                        // columns don't show through. Match the row's tint
                        // (including hover/expanded) so the pin blends in.
                        // Solid (opaque) backgrounds only — translucent tints
                        // let scrolled columns show through the pinned cell.
                        const pinnedBg = item.tracking_label === 'Archive'
                          ? 'bg-gray-100'
                          : selectedIds.includes(item.id)
                          ? 'bg-blue-50'
                          : expandedRow === item.id
                          ? 'bg-blue-50 group-hover:bg-blue-50'
                          : 'bg-white group-hover:bg-blue-50';
                        if (column.key === 'title') {
                          return (
                            <td key={column.key} style={isPinned ? pinnedCellStyle : undefined} className={`px-2 py-1 truncate text-sm text-gray-900 ${colWidth(column)} ${isPinned ? pinnedBg : ''}`}>
                              <div className="flex items-center gap-1 group truncate">
                                <span className="truncate">
                                  {type === 'song' || type === 'album' ? item.title : item.name}
                                </span>

                              </div>
                            </td>
                          );
                        }

                        if (column.key === 'artist' || column.key === 'name') {
                          // On mobile, fold the priority into this pinned cell as
                          // a compact dropdown, so its column can be dropped.
                          const prioritySelect = mergeTrackingIntoFirst && (
                            <select
                              value={item.tracking_label || ''}
                              onChange={(e) => onTrackingLabelChange(item.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className={`appearance-none mt-1 text-[9px] font-medium rounded-full border-0 ring-1 ring-inset ring-gray-200 px-1.5 py-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${getTrackingLabelColor(item.tracking_label)}`}
                            >
                              {trackingLabelOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          );
                          // On mobile (song/album), fold the title into this
                          // pinned cell: title bold on top, artist muted below.
                          if (mergeTitleIntoArtist) {
                            return (
                              <td key={column.key} style={isPinned ? pinnedCellStyle : undefined} className={`px-2 py-1 text-sm text-gray-900 text-left ${colWidth(column)} ${isPinned ? pinnedBg : ''}`}>
                                <div className="font-medium text-[13px] leading-tight truncate" title={item.title}>{item.title}</div>
                                <div className="text-[11px] text-gray-500 leading-tight truncate" title={item.artist}>{item.artist}</div>
                                {prioritySelect}
                                {mergeTrackingIntoFirst && renderUpdatedBy(item)}
                              </td>
                            );
                          }
                          return (
                            <td key={column.key} style={isPinned ? pinnedCellStyle : undefined} className={`px-2 py-1 text-sm text-gray-900 text-left ${colWidth(column)} ${isPinned ? pinnedBg : ''}`}>
                              <div className="truncate">{type === 'song' || type === 'album' ? item.artist : item.name}</div>
                              {prioritySelect}
                              {mergeTrackingIntoFirst && renderUpdatedBy(item)}
                            </td>
                          );
                        }

                        if (column.key === 'tracking_label') {
                          return (
                            <td key={column.key} style={isPinned ? pinnedCellStyle : undefined} className={`px-2 py-1 whitespace-nowrap text-[10px] ${colWidth(column)} ${isPinned ? pinnedBg : ''}`}>
                              <select
                                value={item.tracking_label || ''}
                                onChange={(e) => onTrackingLabelChange(item.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className={`appearance-none text-center w-full text-[10px] font-medium rounded-full border-0 ring-1 ring-inset ring-gray-200 px-2 py-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${getTrackingLabelColor(item.tracking_label)}`}
                              >
                                {trackingLabelOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {renderUpdatedBy(item)}
                            </td>
                          );
                        }

                        // Standalone "Set By" column (e.g. artist-aggregated).
                        if (column.isUpdatedBy) {
                          return (
                            <td key={column.key} style={isPinned ? pinnedCellStyle : undefined} className={`px-2 py-1 text-[10px] text-center ${colWidth(column)} ${isPinned ? pinnedBg : ''}`}>
                              {renderUpdatedBy(item) || <span className="text-gray-300">—</span>}
                            </td>
                          );
                        }

                        // Merged streams/% cell: number on top, % pill below.
                        if (column.merged) {
                          return (
                            <td
                              key={column.key}
                              className={`${isMobile ? 'px-1.5' : 'px-2.5'} py-1.5 whitespace-nowrap text-right ${colWidth(column)} ${isPinned ? pinnedBg : ''}`}
                            >
                              <div className="text-[12px] font-bold tabular-nums text-gray-900 leading-tight">
                                {formatCompact(item[column.key] || 0)}
                              </div>
                              <div className="mt-0.5">
                                <span className={`inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold ${getPercentPill(item[column.pctKey])}`}>
                                  {formatPercent(item[column.pctKey], true)}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        const isChangePct = column.isPercent && column.label !== 'US %';
                        const isWeeksGrown = column.key.includes('weeks_grown');
                        return (
                          <td
                            key={column.key}
                            style={isPinned ? pinnedCellStyle : undefined}
                            className={`${isMobile ? 'px-1.5' : 'px-2.5'} py-2 whitespace-nowrap text-[11px] tabular-nums ${isPinned ? pinnedBg : ''} ${
                              column.isBoolean ? 'text-center' : 'text-right'
                            } ${
                              isWeeksGrown ? `${getWeeksGrownColor(item[column.key])} font-medium` :
                              column.isBoolean ? `${getBooleanColor(item[column.key])} font-medium` :
                              'text-gray-800'
                            } ${colWidth(column)}`}
                          >
                            {column.isBoolean ?
                              (item[column.key] ? '✓' : '✗') :
                            column.isDate ?
                              formatDate(item[column.key]) :
                            isChangePct ? (
                              <span className={`inline-flex items-center justify-end px-1.5 py-0.5 rounded-md font-semibold ${getPercentPill(item[column.key])}`}>
                                {formatPercent(item[column.key], true)}
                              </span>
                            ) :
                            column.isPercent ?
                              formatPercent(item[column.key], false) :
                            column.key.includes('streams') ? (
                              isMobile
                                ? <span className="font-bold text-gray-900">{formatCompact(item[column.key] || 0)}</span>
                                : formatNumber(item[column.key] || 0)
                            ) :
                              column.key === 'song_count' ?
                                formatNumber(item[column.key] || 0) :
                                item[column.key]}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Expanded row. The inner wrapper is sticky to the left of
                        the scroll container and given an explicit pixel width
                        equal to the visible container, so the Chart.js canvas
                        never grows to the full (scrolled) table width and the
                        content stays in view during horizontal scroll. */}
                    {expandedRow === item.id && containerWidth > 0 && (
                      <tr>
                        <td
                          colSpan={displayColumns.length + (enableSelection ? 1 : 0)}
                          className="p-0"
                          style={{ position: 'relative' }}
                        >
                          <div
                            style={{
                              position: 'sticky',
                              left: 0,
                              width: containerWidth,
                              maxWidth: containerWidth,
                              overflow: 'hidden',
                            }}
                            className="px-1 py-2 sm:px-4 sm:py-4 bg-slate-50/60 border-t border-gray-100"
                          >
                            {/* Tabs for albums only */}
                            {type === 'album' && (
                              <div className="border-b border-gray-200 mb-4">
                                <nav className="-mb-px flex space-x-8">
                                  <button
                                    onClick={() => setExpandedRowTab('metrics')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                      expandedRowTab === 'metrics'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                  >
                                    Metrics Chart
                                  </button>
                                  <button
                                    onClick={() => setExpandedRowTab('songs')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                      expandedRowTab === 'songs'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                  >
                                    Album Songs
                                  </button>
                                </nav>
                              </div>
                            )}

                            {(type !== 'album' || expandedRowTab === 'metrics') && renderMetricsContent(item)}

                            {type === 'album' && expandedRowTab === 'songs' && (
                              <div className="space-y-4">
                                <h4 className="text-lg font-medium text-gray-900">
                                  Songs on {item.title}
                                </h4>
                                {isAlbumSongsFetching && !albumSongs ? (
                                  <div className="text-center py-8 text-gray-500">Loading songs...</div>
                                ) : albumSongs && albumSongs.data && albumSongs.data.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead>
                                        <tr>
                                          <th
                                            onClick={() => handleAlbumSongsSort('title')}
                                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                          >
                                            Song<AlbumSongsSortIcon columnKey="title" />
                                          </th>
                                          <th
                                            onClick={() => handleAlbumSongsSort('global_last_complete_week_streams')}
                                            className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                          >
                                            Global Last Week<AlbumSongsSortIcon columnKey="global_last_complete_week_streams" />
                                          </th>
                                          <th
                                            onClick={() => handleAlbumSongsSort('global_week_over_week_change_percent')}
                                            className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                          >
                                            Global %<AlbumSongsSortIcon columnKey="global_week_over_week_change_percent" />
                                          </th>
                                          <th
                                            onClick={() => handleAlbumSongsSort('us_last_complete_week_streams')}
                                            className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                          >
                                            US Last Week<AlbumSongsSortIcon columnKey="us_last_complete_week_streams" />
                                          </th>
                                          <th
                                            onClick={() => handleAlbumSongsSort('us_week_over_week_change_percent')}
                                            className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                          >
                                            US %<AlbumSongsSortIcon columnKey="us_week_over_week_change_percent" />
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {albumSongs.data.map(song => (
                                          <tr key={song.id} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-sm text-gray-900">{song.title}</td>
                                            <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                              {formatNumber(song.global_last_complete_week_streams)}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-right">
                                              <span className={`inline-flex items-center justify-end px-1.5 py-0.5 rounded-md font-semibold ${getPercentPill(song.global_week_over_week_change_percent)}`}>
                                                {song.global_week_over_week_change_percent}%
                                              </span>
                                            </td>
                                            <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                              {formatNumber(song.us_last_complete_week_streams)}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-right">
                                              <span className={`inline-flex items-center justify-end px-1.5 py-0.5 rounded-md font-semibold ${getPercentPill(song.us_week_over_week_change_percent)}`}>
                                                {song.us_week_over_week_change_percent}%
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-center py-8 text-gray-500">
                                    No songs linked to this album yet.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            <div ref={observerTarget} className="h-4" />
{isFetchingNextPage && (
  <div className="text-center py-4">Loading more...</div>
)}
          </tbody>
        </table>
      </div>
      <div ref={observerTarget} className="h-10" />

      {(openFilterKey === 'tracking_label' || openFilterKey === 'updated_by') && (
        <TrackingFilterPopover
          value={columnFilters?.[openFilterKey]}
          labelOptions={openFilterKey === 'tracking_label' ? trackingLabelOptions : null}
          userOptions={userOptions}
          anchorRect={filterAnchorRect}
          onApply={(v) => updateColumnFilter(openFilterKey, v)}
          onClose={() => { setOpenFilterKey(null); setFilterAnchorRect(null); }}
        />
      )}

      {openFilterKey && openFilterKey !== 'tracking_label' && openFilterKey !== 'updated_by' && (() => {
        // Prefer the displayColumns entry so a merged column carries pctKey.
        const col = displayColumns.find((c) => c.key === openFilterKey) || columns.find((c) => c.key === openFilterKey);
        if (!col) return null;
        const pctColumn = col.merged ? { key: col.pctKey, label: col.pctLabel } : null;
        return (
          <ColumnFilterPopover
            column={col}
            value={columnFilters?.[openFilterKey]}
            pctColumn={pctColumn}
            pctValue={pctColumn ? columnFilters?.[pctColumn.key] : undefined}
            anchorRect={filterAnchorRect}
            onApply={updateColumnFilter}
            onClose={() => { setOpenFilterKey(null); setFilterAnchorRect(null); }}
          />
        );
      })()}

      {sortMenuCol && sortMenuRect && (
        <MergedSortMenu
          column={sortMenuCol}
          anchorRect={sortMenuRect}
          sortConfig={sortConfig}
          onSelect={applyMergedSort}
          onClose={() => { setSortMenuCol(null); setSortMenuRect(null); }}
        />
      )}
    </div>
  );
}

// Categorical filter for the Priority column: multi-select Priority labels and
// multi-select Tracked-by users. Either or both. Requires an explicit Apply.
function TrackingFilterPopover({ value, labelOptions, userOptions, anchorRect, onApply, onClose }) {
  const popoverRef = useRef(null);
  const [labels, setLabels] = useState(value?.labels || []);
  const [userIds, setUserIds] = useState(value?.userIds || []);

  useEffect(() => {
    setLabels(value?.labels || []);
    setUserIds(value?.userIds || []);
  }, [value?.labels, value?.userIds]);

  useEffect(() => {
    function handler(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  if (!anchorRect) return null;

  // Priority label values (skip the 'None' placeholder option; add an explicit
  // "No priority" match instead).
  const labelValues = (labelOptions || [])
    .map((o) => o.value)
    .filter((v) => v && v !== 'None');
  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const WIDTH = 220;
  const top = anchorRect.bottom + 4;
  const left = Math.max(8, Math.min(anchorRect.right - WIDTH, window.innerWidth - WIDTH - 8));

  const apply = (e) => {
    e.stopPropagation();
    onApply({ labels, userIds });
    onClose();
  };
  const clear = (e) => {
    e.stopPropagation();
    setLabels([]);
    setUserIds([]);
    onApply({ labels: [], userIds: [] });
    onClose();
  };

  return createPortal(
    <div
      ref={popoverRef}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top, left, width: WIDTH, zIndex: 50 }}
      className="bg-white border border-gray-200 rounded-md shadow-lg p-3 normal-case tracking-normal"
    >
      {labelValues.length > 0 && (
        <>
          <div className="text-[11px] font-semibold text-gray-700 mb-1">Priority</div>
          <div className="max-h-32 overflow-y-auto mb-2">
            {labelValues.map((v) => (
              <label key={v} className="flex items-center gap-2 text-[11px] text-gray-700 py-0.5 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={labels.includes(v)} onChange={() => setLabels((a) => toggle(a, v))} />
                {v}
              </label>
            ))}
            <label className="flex items-center gap-2 text-[11px] text-gray-400 py-0.5 cursor-pointer">
              <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={labels.includes('None')} onChange={() => setLabels((a) => toggle(a, 'None'))} />
              No priority
            </label>
          </div>
        </>
      )}

      <div className={`text-[11px] font-semibold text-gray-700 mb-1 ${labelValues.length > 0 ? 'pt-1 border-t border-gray-100' : ''}`}>Tracked by</div>
      <div className="max-h-32 overflow-y-auto">
        {(userOptions || []).map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-[11px] text-gray-700 py-0.5 cursor-pointer">
            <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={userIds.includes(u.id)} onChange={() => setUserIds((a) => toggle(a, u.id))} />
            <span className={`truncate ${u.is_active === false ? 'text-gray-400 italic' : ''}`} title={u.email}>
              {emailName(u.email)}{u.is_active === false ? ' (former)' : ''}
            </span>
          </label>
        ))}
        <label className="flex items-center gap-2 text-[11px] text-gray-400 py-0.5 cursor-pointer">
          <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={userIds.includes('None')} onChange={() => setUserIds((a) => toggle(a, 'None'))} />
          Unassigned
        </label>
      </div>

      <div className="flex justify-between items-center pt-2 mt-1 border-t border-gray-100">
        <button type="button" onClick={clear} className="text-[10px] text-gray-500 hover:text-gray-700">Clear</button>
        <button type="button" onClick={apply} className="text-[11px] px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Apply</button>
      </div>
    </div>,
    document.body
  );
}

// Dropdown for sorting a merged streams/% column — pick metric + direction.
function MergedSortMenu({ column, anchorRect, sortConfig, onSelect, onClose }) {
  const menuRef = useRef(null);
  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const WIDTH = 184;
  const top = anchorRect.bottom + 4;
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - WIDTH - 8));

  const opts = [
    { key: column.key, label: column.label },
    { key: column.pctKey, label: column.pctLabel },
  ];

  return createPortal(
    <div
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top, left, width: WIDTH, zIndex: 50 }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 normal-case tracking-normal"
    >
      {opts.map((o) => {
        const active = sortConfig?.key === o.key;
        // First click on a metric → desc; clicking the active one again → asc.
        const nextOrder = active && sortConfig?.order === 'desc' ? 'asc' : 'desc';
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onSelect(o.key, nextOrder)}
            className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs hover:bg-blue-50 ${active ? 'text-blue-600 font-semibold bg-blue-50/60' : 'text-gray-700'}`}
          >
            <span>{o.label}</span>
            {active && (
              sortConfig?.order === 'desc'
                ? <ArrowDownIcon className="h-3.5 w-3.5" />
                : <ArrowUpIcon className="h-3.5 w-3.5" />
            )}
          </button>
        );
      })}
    </div>,
    document.body
  );
}
